import { useEffect, useRef, useState } from "react";
import "highlight.js/styles/github.css";
import ExampleQuestions from "@/components/ExampleQuestions.tsx";
import { aiApi } from "@/api/aiApi.ts";
import {
  type AIEvent,
  AIEventType,
  type AIResponse,
  type Citation,
  type ConversationParams,
  type CreateQuestionRequest,
  FeedbackDisLikeType,
  FeedbackLikeType,
  type GetGoodFeedbackParams,
  type HistoryList,
  type keyOfValue,
  type OldResponse,
  type SearchDocument,
  type SearchEventData,
  type TopicRecord,
} from "@/types";
import AuthService from "@/services/auth.ts";
import "./index.css";
import { useNavigate, useParams } from "react-router-dom"; // 确保导入样式文件
import eventBus from "@/utils/eventBus.ts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import Loading from "@/icons/three-dots.svg?react";
import SvgLoading from "@/icons/loadding.svg?react";
import { RenderEvent } from "@/pages/DetailPage/component/RenderEvent.tsx";
import { useScrollManagement } from "@/pages/DetailPage/component/useScrollManagement.ts";
import { ChevronDown, ChevronRight } from "lucide-react";
import TypewriterMarkdown from "@/components/TypewriterMarkdown";
import TopicHeader from "@/components/TopicHeader.tsx";
import { ArrowsPointingInIcon, ArrowsPointingOutIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/16/solid";
import { Button } from "@/components/ui/button.tsx";
import PdfImage from "@/assets/search/pdf.svg?react";
import MdImage from "@/assets/search/ic-MarkDown.svg?react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible.tsx";
import { ViewFile } from "@/pages/DetailPage/component/viewFile.tsx";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import EditTitleDialog from "./component/EditTitleDialog";
import { Separator } from "@/components/ui/separator.tsx";
import { useTranslation } from "@/locales";
import { getTopicDetail } from "@/api/adminApi";

interface viewFileEvent {
  type: string;
  record?: SearchDocument;
  index: number;
}

interface feedbackDetail {
  index: number;
  feedback: string;
  detail: {
    options: string[];
    other: string;
  };
}

export default function AIChatWithMarkdown() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 状态管理
  const [messages, setMessages] = useState<AIResponse[]>([]);

  const [topicNo, setTopicNo] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  // 轮询状态跟踪
  const pollingActive = useRef(false);
  const hasFetched = useRef(false);
  const userInfo = AuthService.getUserInfo();
  const [id, setId] = useState<string | number>(userInfo?.uid ?? 1);
  const { containerRef, scrollToBottom } = useScrollManagement();
  const [userScrolled, setUserScrolled] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [scrollbarVisible, setScrollbarVisible] = useState(false);
  const [current, setCurrent] = useState(0);
  const [topicTitle, setTopicTitle] = useState<string>("");
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [conversationNo, setConversationNo] = useState<string>("");
  const [history, setHistory] = useState<OldResponse | null>(null);
  const [historyFeedback, setHistoryFeedback] = useState<feedbackDetail>({
    index: -1,
    feedback: "",
    detail: {
      options: [],
      other: "",
    },
  });
  const [feedbackList, setFeedbackList] = useState<feedbackDetail>({
    index: -1,
    feedback: "",
    detail: {
      options: [],
      other: "",
    },
  });
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  // const [selectFiles, setselectFiles] = useState<SearchDocument | null>(null);
  const [sheetFiles, setSheetFiles] = useState<SearchDocument[]>([]);
  const [sheetCitations, setSheetCitations] = useState<Citation[]>([]);
  const [pdfConversationNo, setPdfConversationNo] = useState("");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [queryIndex, setQueryIndex] = useState<number | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const { paramsId } = useParams<{ paramsId: string }>();
  // 在组件顶部添加状态管理动画
  const [sheetAnimation, setSheetAnimation] = useState<"entering" | "entered" | "exiting" | "exited">("exited");
  const [sheetVisible, setSheetVisible] = useState(false);
  const [viewFile, setViewFile] = useState<viewFileEvent>({
    type: "",
    index: 1,
  });
  const [mouseLeaveIndex, setMouseLeaveIndex] = useState<number | null>(null);
  const [isShare, setIsShare] = useState(false);
  const [shareList, setShareList] = useState<string[]>([]);
  const [isAllSelect, setIsAllSelect] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [startThink, setStartThink] = useState(false);
  const FeedbackList: keyOfValue[] = [
    {
      key: FeedbackLikeType.ACCURATE,
      value: t("feedback.accurate"),
    },
    {
      key: FeedbackLikeType.EASY_TO_UNDERSTAND,
      value: t("feedback.easyToUnderstand"),
    },
    {
      key: FeedbackLikeType.COMPREHENSIVE,
      value: t("feedback.comprehensive"),
    },
    {
      key: "OTHER",
      value: t("feedback.other"),
    },
  ];
  const FeedbackDisList: keyOfValue[] = [
    {
      key: FeedbackDisLikeType.UNDERSTANDING_ERROR,
      value: t("feedback.understandingError"),
    },
    {
      key: FeedbackDisLikeType.CONTENT_ERROR,
      value: t("feedback.contentError"),
    },
    {
      key: FeedbackDisLikeType.FORMAT_ERROR,
      value: t("feedback.formatError"),
    },
    {
      key: "OTHER",
      value: t("feedback.other"),
    },
  ];
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleMsScroll = () => {
      // 显示滚动条
      setScrollbarVisible(true);
      // 清除之前的超时
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - scrollTop <= clientHeight + 20;

      setUserScrolled(!isAtBottom);
      // 设置新的超时来隐藏滚动条
      timeoutRef.current = setTimeout(() => {
        setScrollbarVisible(false);
      }, 1000); // 1秒后隐藏滚动条
    };

    // 添加滚动事件监听器
    container.addEventListener("scroll", handleMsScroll);

    // 清理函数
    return () => {
      container.removeEventListener("scroll", handleMsScroll);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [containerRef, messages, history]);

  const onStop = () => {
    // pollingActive.current = false;
    aiApi.conversation.postCancel(conversationNo).then(() => {
      // setConversationNo('');
      setIsLoading(false);
    });
  };
  const fetchData = async () => {
    console.log(paramsId);
    if (paramsId) {
      try {
        console.log(1);
        setIsLoading(true);
        const response = (await getTopicDetail(paramsId)) as OldResponse;
        const params = localStorage.getItem("queryParams") ? JSON.parse(localStorage.getItem("queryParams") ?? "") : null;
        const question = localStorage.getItem("question");
        response.records?.map((item) => {
          item.isOpen = false;
        });
        setHistory(response);
        if (response && response.records && response.records.length > 0) {
          if (response.topicName) {
            document.title = response.topicName;
          }
          setTimeout(() => {
            scrollToBottom();
          }, 100);

          const item = response.records.find((record) => (record.conversationNo === params ? params.conversationNo : ""));
          if (item && item.conversationNo == (params ? params.conversationNo : "") && item.query == question) {
            return setIsLoading(false);
          } else {
            if (params && question) {
              console.log("question", question);
              setMessages((prev) => [
                ...prev,
                {
                  conversationNo: params.conversationNo,
                  events: [],
                  question: question,
                  isOpen: true,
                },
              ]);
              // 重置轮询状态
              pollingActive.current = true;
              // 开始轮询事件
              simulateAIResponse(params?.conversationNo, question);
            }
          }
        } else {
          console.log(3);
          console.log(params);
          console.log(question);
          if (params && question) {
            setMessages((prev) => [
              ...prev,
              {
                conversationNo: params.conversationNo,
                events: [],
                question: question,
                isOpen: true,
              },
            ]);
            // 重置轮询状态
            pollingActive.current = true;
            // 开始轮询事件
            console.log(question);
            console.log("addHistory event received");
            eventBus.emit("addHistory");
            simulateAIResponse(params?.conversationNo, question);
          }
        }
      } catch (error) {
        console.error("初始化失败:", error);
        setIsLoading(false);
      }
    }
  };

  const handlePdfClick = (payload: { citation?: Citation; doc?: SearchDocument; conversationNo?: string }) => {
    console.log("接收到 pdfClick 事件，参数为:", payload);
    const params = new URLSearchParams();
    console.log(conversationNo);
    if (payload.conversationNo) {
      params.append("conversationNo", payload.conversationNo);
    }
    if (payload.citation) {
      // 假设 citation 有 id 或 index 字段，你可以根据实际情况拼接
      if (payload.citation.fileId !== undefined) {
        params.append("fileId", payload.citation.fileId.toString());
      }
      if (payload.citation.index !== undefined) {
        params.append("citationIndex", payload.citation.index.toString());
      }
      if (payload.citation.page !== undefined) {
        params.append("page", payload.citation.page.toString());
      }
      // 如果需要传更多字段，可以继续添加
    }
    if (payload.doc) {
      params.append("type", payload.doc.type.toString());
    }
    const currentUrl = window.location.href;
    const currentPathIndex = currentUrl.indexOf("#");
    const baseUrl = currentPathIndex !== -1 ? currentUrl.substring(0, currentPathIndex) : currentUrl.replace(/\/?$/, "/");
    const url = `${baseUrl}#/special?${params.toString()}`;
    console.log(url);
    window.open(url, "_blank");
  };

  const handleViewFile = (payload: { citation?: Citation; doc?: SearchDocument; conversationNo?: string }) => {
    console.log("isShare", isShare);
    setIsSheetOpen(false);

    if (isShare) return handleClose();
    console.log(payload);
    if (payload && payload.doc && payload.doc?.fileId && payload.doc.type) {
      setIsViewOpen(true);

      eventBus.emit("sidebarChange", false);
      eventBus.emit("setPdfPage", payload.citation?.page ?? 1);
      setViewFile({
        type: payload.doc.type,
        record: payload.doc,
        index: payload.citation?.page ?? 1,
      });
    }
  };

  const handleClose = () => {
    setViewFile({
      type: "",
      index: 1,
    });
    setIsViewOpen(false);
    eventBus.emit("sidebarChange", true);
  };
  useEffect(() => {
    // 在这里执行状态更新后的逻辑
  }, [isViewOpen]);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setHistory({
      records: [],
      topicName: "",
      topicNo: "",
    });
    if (paramsId) {
      setTopicNo(paramsId);
    }
    if (paramsId && topicNo && paramsId !== topicNo) {
      console.log("paramsId == topicNo");
      localStorage.setItem("question", "");
      pollingActive.current = false;
      handleSheetClose();
      setIsAllSelect(false);
      setIsShare(false);
      setShareList([]);
      setIsViewOpen(false);
      setIsSheetOpen(false);
      setMessages([]);
      setHistory({
        records: [],
        topicName: "",
        topicNo: "",
      });
      closeFeedback();
    }
    fetchData().then(() => {
      setIsLoading(false);
      hasFetched.current = false;
    });
    // 这里可以根据 id 请求数据
  }, [paramsId]);

  useEffect(() => {
    if (userInfo?.uid) {
      setId(userInfo.uid ?? 1);
    }
    const handleSearch = (query: string) => {
      fetchAnswerForQuestion(query);
    };
    const editTitle = () => {
      setIsEdit(true);
    };

    eventBus.on("search", handleSearch);
    // eventBus.on('pdfClick', handleViewFile);
    eventBus.on("editTitle", editTitle);
    return () => {
      eventBus.off("search", handleSearch);
      // eventBus.off('pdfClick', handleViewFile);
      eventBus.off("editTitle", editTitle);
    };
  }, []);

  useEffect(() => {
    eventBus.on("pdfClick", handleViewFile);
    return () => {
      eventBus.off("pdfClick", handleViewFile);
    };
  }, [isShare]);
  useEffect(() => {
    const load = () => {
      pollingActive.current = false;
      localStorage.setItem("question", "");
      navigate("/");
      setMessages([]);
      setHistory({
        records: [],
        topicName: "",
        topicNo: "",
      });
    };

    eventBus.on("load", load);

    return () => {
      eventBus.off("load", load);
    };
  });

  useEffect(() => {
    if (containerRef.current) {
      if (!userScrolled) {
        scrollToBottom();
      }
    }
  }, [current]);
  const fetchAnswerForQuestion = async (question: string) => {
    console.log("收到", question);
    localStorage.setItem("question", question);
    if (!question.trim()) return;
    if (!id) {
      if (userInfo?.uid) setId(userInfo.uid ?? 1);
    }
    try {
      setIsLoading(true);
      const params: ConversationParams = {
        question: question,
        topicNo: topicNo,
      };
      const res = (await aiApi.conversation.create(params)) as CreateQuestionRequest | null;

      if (res) {
        if (!paramsId) navigate(`#/${res.topicNo}`);
        setMessages((prev) => [
          ...prev,
          {
            conversationNo: res.conversationNo,
            events: [],
            question: question,
            isOpen: true,
          },
        ]);
        setTopicNo(res.topicNo);

        // 重置轮询状态
        pollingActive.current = true;

        // 开始轮询事件
        simulateAIResponse(res.conversationNo, question);
      }
    } catch (error) {
      console.error("初始化失败:", error);
      setIsLoading(false);
    }
  };

  // 模拟返回 Markdown 格式的 AI 回答
  const simulateAIResponse = async (conversation: string, question: string) => {
    if (!conversation || !pollingActive.current) return;
    let continuePolling = true;
    setIsLoading(true);
    scrollToBottom();
    setConversationNo(conversation);
    localStorage.removeItem("question");
    while (continuePolling && pollingActive.current) {
      try {
        const response = (await aiApi.conversation.getPoll(conversation)) as AIEvent[];
        if (response.length > 0) {
          setMessages((prev) => {
            const existingIndex = prev.findIndex((msg) => msg.conversationNo === conversation);
            if (existingIndex !== -1) {
              const updatedMessages = [...prev];
              const existingEvents = updatedMessages[existingIndex].events;

              // 处理新事件，避免重复添加相同类型事件，改为覆盖 data
              const mergedEvents = [...existingEvents];
              response.forEach((newEvent) => {
                const eventIndex = mergedEvents.findIndex((e) => e.event === newEvent.event);
                if (eventIndex !== -1) {
                  mergedEvents[eventIndex] = newEvent;
                } else {
                  // 没有该类型事件则添加
                  mergedEvents.push(newEvent);
                }
              });
              const think = response.some((event) => event.event === AIEventType.THINK);
              const answer = response.some((event) => event.event === AIEventType.ANSWER);

              updatedMessages[existingIndex] = {
                conversationNo: conversation,
                events: mergedEvents,
                question: updatedMessages[existingIndex].question,
                isOpen: updatedMessages[existingIndex].isOpen,
                isThinkEnd: answer && !think ? true : updatedMessages[existingIndex].isThinkEnd,
              };
              console.log(updatedMessages);
              return updatedMessages;
            } else {
              const message = [
                ...prev,
                {
                  conversationNo: conversation,
                  events: response,
                  question: question,
                  isOpen: true,
                },
              ];
              console.log(message);

              return message;
            }
          });
        }
        if (response.some((event) => event.event === AIEventType.THINK)) {
          setStartThink(true);
        }

        const hasEndEvent = response.some((event) => event.event === AIEventType.END);

        if (hasEndEvent) {
          console.log("END");
          continuePolling = false;
          setStartThink(false);

          setTimeout(() => {
            setIsLoading(false);
            scrollToBottom();
          }, 500);
          const endEvent = response.filter((event) => event.event === AIEventType.END);
          if ("stopReason" in endEvent[0].data && endEvent[0].data.stopReason === "COMPLETE") {
            setTimeout(() => {
              if (!topicTitle) {
                getTopicTitle().then(eventBus.emit("addHistory"));
              }
            }, 2000);
          }
        } else {
          await new Promise((resolve) => setTimeout(resolve, 1200));
          console.log("继续轮询...");
        }
      } catch (error) {
        console.error("轮询过程中出错:", error);
        continuePolling = false;
        setIsLoading(false);
        setMessages((prev) => {
          const existingIndex = prev.findIndex((msg) => msg.conversationNo === conversation);

          if (existingIndex !== -1) {
            const updatedMessages = [...prev];
            updatedMessages[existingIndex] = {
              ...updatedMessages[existingIndex],
              events: [...updatedMessages[existingIndex].events],
            };
            return updatedMessages;
          }
          return prev;
        });
      }
    }
    setTimeout(() => {
      scrollToBottom();
    }, 500);
    pollingActive.current = false;
  };

  const MAX_RETRY = 4;

  const getTopicTitle = async () => {
    if (topicTitle || !paramsId) return;

    let retryCount = 0;
    while (retryCount < MAX_RETRY && !topicTitle) {
      console.log("开始获取标题，尝试次数:", retryCount + 1);
      try {
        const record = (await aiApi.conversation.getTitle(paramsId)) as TopicRecord;
        if (record && record.topicName) {
          setTopicTitle(record.topicName);
          if (record.topicName) {
            document.title = record.topicName;
          }
          break;
        } else {
          retryCount++;
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.error("获取标题失败:", error);
        retryCount++;
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
  };

  // 发送消息处理函数
  const handleSend = async (value: string): Promise<void> => {
    console.log(topicNo);
    if (isLoading) return;
    if (!value.trim()) return;
    // 添加用户消息
    setIsLoading(true);
    try {
      const userInfo = AuthService.getUserInfo();
      if (!userInfo) {
        return;
      }
      const res = (await aiApi.conversation.create({
        question: value,
        topicNo: topicNo || paramsId,
      } as ConversationParams)) as CreateQuestionRequest | null;
      if (res) {
        pollingActive.current = true;
        setMessages((prev) => [
          ...prev,
          {
            conversationNo: res.conversationNo,
            events: [],
            question: value,
            isOpen: true,
          },
        ]);
        setTimeout(() => {
          const questionTexts = document.querySelectorAll(".question-text");
          if (questionTexts.length > 0) {
            const lastQuestionText = questionTexts[questionTexts.length - 1] as HTMLElement;
            // 先移除已有撑高元素，避免重复插入
            const existingSpacer = lastQuestionText.querySelector(".height-spacer");
            if (existingSpacer) {
              existingSpacer.remove();
            }
            // 创建撑高元素
            const spacer = document.createElement("div");
            spacer.className = "height-spacer";
            const header = document.querySelector(".header");
            const paddingTop = 48; // 根据实际情况调整
            const headerHeight = header ? header.offsetHeight : 0;
            const viewportHeight = window.innerHeight;
            const spacerHeight = viewportHeight - headerHeight - paddingTop - 165;
            Object.assign(spacer.style, {
              position: "absolute",
              top: "0",
              left: "0",
              width: "100%",
              height: `${spacerHeight}px`,
              pointerEvents: "none",
              zIndex: "0",
            });
            lastQuestionText.style.position = "relative"; // 确保父元素定位
            lastQuestionText.appendChild(spacer);
          }
          const questionElements = document.querySelectorAll(".question-text-content");
          if (questionElements.length > 0) {
            const el = questionElements[questionElements.length - 1];
            console.log(el);
            if (el instanceof HTMLElement) {
              el.scrollTop = 0; // 滚动到顶部
            }
          }
        }, 300);
        setTimeout(() => {
          scrollToBottom();
        }, 500);

        await simulateAIResponse(res?.conversationNo, value);
      }
    } catch (error) {
      console.error("发送消息失败:", error);
      setIsLoading(false);
    }
  };
  // 自动滚动到底部

  const recommendEvent = (recommend: string[]) => {
    return <ExampleQuestions onSelect={handleSend} list={recommend} />;
  };

  const messagesCitations = (message: AIResponse) => {
    let citations: Citation[] = [];
    message.events.forEach((event) => {
      if (event.event === AIEventType.SEARCH && "citations" in event.data) {
        citations = event.data.citations;
      }
    });
    return citations;
  };
  const messageDocs = (message: AIResponse) => {
    let docs: SearchDocument[] = [];
    message.events.forEach((event) => {
      if (event.event === AIEventType.SEARCH && "docs" in event.data) {
        docs = event.data.docs;
      }
    });
    console.log(docs);
    return docs;
  };

  const historyLength = history && history.records ? history.records.length : 0;
  const handleClick = (data: SearchEventData, conversationNo: string) => {
    console.log("isShare", isShare);
    if (isShare) return handleSheetClose();
    setSheetFiles(data.docs);
    setSheetCitations(data.citations);
    setPdfConversationNo(conversationNo);
    setIsSheetOpen(true);
    setIsViewOpen(false);
  };

  // 关闭侧边弹窗
  const handleSheetClose = () => {
    setSheetAnimation("exiting");
    setTimeout(() => {
      setIsSheetOpen(false);
      setSheetAnimation("exited");
      setSheetVisible(false);
    }, 200);
  };
  useEffect(() => {
    if (isSheetOpen) {
      setSheetVisible(true);
      setSheetAnimation("entering");
      setTimeout(() => setSheetAnimation("entered"), 100);
    } else if (sheetVisible) {
      handleSheetClose();
    }
  }, [isSheetOpen]);
  const handleCopy = async (index: number, value: string) => {
    const textArea = document.createElement("textarea");
    const safeValue = (value ?? "").replace(/\[\^(\d+)\]/g, '"$1"');
    textArea.value = safeValue;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);

    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 1000);
  };

  const handleQueryCopy = (index: number, value: string) => {
    console.log("value", value);
    const textArea = document.createElement("textarea");
    textArea.value = value ?? "";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
    setQueryIndex(index);
    setTimeout(() => setQueryIndex(null), 3000); // 3秒后恢复
  };

  const handleSelect = () => {
    const list: string[] = [];
    if (isAllSelect) {
      setIsAllSelect(false);
    } else {
      if (history.records) {
        history.records.map((item) => {
          list.push(item.conversationNo);
        });
      }
      messages.forEach((item) => {
        list.push(item.conversationNo);
      });
      setIsAllSelect(true);
    }

    setShareList(list);
  };

  const sheet = () => {
    const fileIds: number[] = [];
    sheetCitations.forEach((citation) => {
      if (!fileIds.includes(citation.fileId)) {
        fileIds.push(citation.fileId);
      }
    });

    // 匹配文件，分为引用文件和未引用文件
    const referencedFiles = sheetFiles.filter((item) => fileIds.includes(item.fileId));

    return (
      <aside className={`sheet-container ${sheetAnimation} rounded-r-16`}>
        <div className={"flles"}>
          <div className={"files_list"}>
            <div className="close-btn">
              <button className="text-token-text-secondary hover:bg-[#e8e8e8] rounded-lg h-[32px] w-[32px] mr-1 flex items-center justify-center" onClick={handleSheetClose}>
                <XMarkIcon className="w-[22px] h-[22px]"></XMarkIcon>
              </button>
            </div>
            <Collapsible defaultOpen={true}>
              <CollapsibleTrigger>
                <div className={"mt-[24px] mb-[5px]"}>
                  {t("chat.referencedFiles")}
                  {referencedFiles.length ? `(${referencedFiles.length})` : ""}
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                {referencedFiles.map((file, index) => (
                  <div className={"label-item"} key={file.fileId}>
                    <div
                      className={"left-citation-content active"}
                      onClick={() => {
                        const citation: Citation = {
                          index: index,
                          page: 1,
                          fileId: file.fileId,
                          summary: "",
                        };
                        handlePdfClick({
                          citation,
                          doc: file,
                          conversationNo: pdfConversationNo,
                        });
                      }}
                    >
                      <div className="flex items-start mb-[4px]">
                        <div className={"flex text-[14px]"}>{file.type == "PDF" ? <PdfImage className="w-[16px] h-[16px] mr-1" /> : <MdImage className="w-[16px] h-[16px] mr-1" />}</div>
                        <div className="text-[14px] text-[var(--text-text_primary)] font-medium truncate-2-lines">{`${file?.fileName}`}</div>
                      </div>
                      <div className="flex items-center gap-1 overflow-hidden">
                        {file && file?.labels
                          ? file?.labels.map((label, index) => {
                              return (
                                <HoverTip key={`label-${label.key}-${file?.fileId}-${Math.random()}`} content={label.value || ""} position="bottom">
                                  <div className="inline-flex items-center text-center  px-[8px] py-[4px] bg-[#FAFAFA] hover:bg-gray-300 border border-[#E6E6E6] rounded-md text-[11px] leading-[11px] text-gray-700 transition-colors cursor-pointer max-w-[90px] min-w-0 justify-center dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 dark:text-gray-300">
                                    <span className="truncate">{label.value || ""}</span>
                                  </div>
                                </HoverTip>
                              );
                            })
                          : null}
                      </div>
                      <div className="text-[#6A6A6A] text-[12px] truncate-3-lines">{file.summary}</div>
                      <div className="flex justify-between text-[12px] text-[#8A8A8A] mt-2">
                        <div>收錄於 {file?.knowledgeName || ""}</div>
                        <div>{file?.inclusionDate || ""}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </aside>
    );
  };

  const historyThink = (event: HistoryList, index: number) => {
    const changeOpen = () => {
      setHistory((prev) => {
        if (!prev.records) return prev;

        const newRecords = [...prev.records];
        newRecords[index] = {
          ...newRecords[index],
          isOpen: !newRecords[index].isOpen,
        };

        return { ...prev, records: newRecords };
      });
    };

    const thinksStop = event.stopReason != "COMPLETE" && !event.answer && event.reasoning.length > 0;
    if (event.isOpen) {
      return (
        <div className="pt-0 ai overflow-hidden">
          <div className={"think-button-collapse collapse-collapse-button-Lf stick-LY h-full min-h-[24px]"}>
            <div
              className="flex justify-between items-center w-full"
              onClick={(e) => {
                e.stopPropagation();
                changeOpen();
              }}
            >
              <div className="think-block-title">{thinksStop ? t("think.stopped") : t("think.completed")}</div>
              <Button variant="outline" className="z-10 text-[22px] h-[28px] w-[28px]">
                <ArrowsPointingInIcon className={"fill-[var(--text-text_primary)]"} />
              </Button>
            </div>
          </div>
          <div className={"think think-container"} style={{ maxHeight: "100%" }}>
            <div className="content think-content">
              {/*渲染数据*/}
              <TypewriterMarkdown content={event.reasoning} speed={10} citations={event.citations} docs={event.files} conversationNo={event.conversationNo} />
            </div>
          </div>
          {/*<div ref={bottomRef}/>*/}
        </div>
      );
    } else {
      return (
        <Button
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            changeOpen();
          }}
        >
          <div className="think-block-title">
            <span>{thinksStop ? t("think.stopped") : t("think.completed")}</span>
          </div>
          <ArrowsPointingOutIcon />
        </Button>
      );
    }
  };

  const handleMouseout = (index: number) => {
    console.log("要进来了吗");
    console.log(index);
    setMouseLeaveIndex(index);
  };
  const handleMouseLeave = () => {
    console.log("要出来了吗");
    setMouseLeaveIndex(null);
  };

  const handleToggleItem = (id: string) => {
    if (!isShare) return;
    setShareList((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  useEffect(() => {
    if (!history) return;
    const totalCount = (history.records?.length ?? 0) + messages.length;
    // 判断是否全选
    if (shareList.length === totalCount && totalCount > 0) {
      setIsAllSelect(true);
    } else {
      setIsAllSelect(false);
    }
  }, [shareList]);

  async function onHistoryFeedback(record: HistoryList, feedback: string) {
    console.log(record);
    const res = (await aiApi.conversation.getFeedback({
      conversationNo: record.conversationNo,
      feedback: feedback,
      detail: {
        options: [],
        other: "",
      },
    })) as { result: string };
    if (res) {
      console.log(res);
      setHistory((prve) => ({
        ...prve,
        records: prve.records?.map((item, index) => {
          if (item.conversationNo === record.conversationNo) {
            if (res.result !== "NONE") {
              setHistoryFeedback({
                index: index,
                feedback: res.result,
                detail: {
                  options: [],
                  other: "",
                },
              });
            } else {
              setHistoryFeedback({
                index: -1,
                feedback: "",
                detail: {
                  options: [],
                  other: "",
                },
              });
            }
            return { ...item, feedback: res.result };
          }
          return item;
        }),
      }));
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollBy({ top: 40, behavior: "smooth" });
        }
      }, 500);
    }
  }

  async function onMessagesFeekback(record: AIResponse, feedback: string) {
    console.log(record);
    const res = (await aiApi.conversation.getFeedback({
      conversationNo: record.conversationNo,
      feedback: feedback,
      detail: {
        options: [],
        other: "",
      },
    })) as { result: string };
    if (res) {
      console.log(res);
      setMessages((prevMessages) =>
        prevMessages.map((msg, index) => {
          if (msg.conversationNo === conversationNo) {
            if (res.result !== "NONE") {
              setFeedbackList({
                index: index,
                feedback: res.result,
                detail: {
                  options: [],
                  other: "",
                },
              });
            } else {
              setFeedbackList({
                index: -1,
                feedback: "",
                detail: {
                  options: [],
                  other: "",
                },
              });
            }
            return {
              ...msg,
              feedback: res.result,
            };
          }
          return msg;
        })
      );
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollBy({ top: 40, behavior: "smooth" });
        }
      }, 500);
    }
  }

  async function onSubmit(record: AIResponse | HistoryList, data: feedbackDetail) {
    const res = (await aiApi.conversation.getFeedback({
      conversationNo: record.conversationNo,
      feedback: record.feedback,
      detail: {
        options: data.detail.options.filter((item) => item != "OTHER"),
        other: data.detail.other,
      },
    } as GetGoodFeedbackParams)) as { result: string };
    if (res) {
      setIsSubmit(true);
      setTimeout(() => {
        setIsSubmit(false);
        setHistoryFeedback({
          index: -1,
          feedback: "",
          detail: {
            options: [],
            other: "",
          },
        });
        setFeedbackList({
          index: -1,
          feedback: "",
          detail: {
            options: [],
            other: "",
          },
        });
      }, 1500);
    }
  }
  function onChangeSuccess(title: string): void {
    document.title = title;
    eventBus.emit("changeTitleSuccess", title);
    setTopicTitle(title);
  }

  function closeFeedback() {
    setIsSubmit(false);
    setHistoryFeedback({
      index: -1,
      feedback: "",
      detail: {
        options: [],
        other: "",
      },
    });
    setFeedbackList({
      index: -1,
      feedback: "",
      detail: {
        options: [],
        other: "",
      },
    });
  }

  return (
    <div className="bg-[var(--background-page_background_web))] flex-1 flex" style={{ borderRadius: 16 }}>
      {messages.length > 0 || historyLength > 0 ? (
        <div className="bg-[var(--background-page_background_web)] flex-1 flex flex-col" style={{ borderRadius: 16 }}>
          <TopicHeader title={topicTitle || history?.topicName} isShare={isShare} onSelect={isAllSelect ? () => {} : handleSelect}></TopicHeader>
          <div className="flex-1 flex overflow-hidden">
            <div className="flex-1">
              <div className="topic-chat-container flex-1 px-4 pt-0 pb-1.5 flex justify-between flex-col">
                <div
                  className={
                    scrollbarVisible
                      ? "scroll-container flex-1 w-full overflow-y-auto pl-auto pr-auto flex flex-col items-center scrolling"
                      : "scroll-container flex-1 full  pl-auto pr-auto flex flex-col items-center"
                  }
                  ref={containerRef}
                >
                  {/*这里注意，可能是信息的开始*/}
                  <div className="messages">
                    {history &&
                      history.records?.map((record, index) => {
                        record.files = record.files.filter((doc) => doc.used === true);

                        return (
                          <div key={record.conversationNo} className="conversation-group mb-5">
                            {/* 问题展示 */}
                            <div className={isShare ? "flex justify-between flex-1 share-content" : "flex justify-end flex-1"}>
                              {isShare && (
                                <Checkbox
                                  id={record.conversationNo}
                                  className={"checked"}
                                  checked={shareList.includes(record.conversationNo)}
                                  onCheckedChange={() => handleToggleItem(record.conversationNo)}
                                ></Checkbox>
                              )}

                              <div
                                className={isShare && shareList.includes(record.conversationNo) ? "check-right flex-1" : "flex-1"}
                                onMouseEnter={() => handleMouseout(index)}
                                onMouseLeave={() => handleMouseLeave()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleItem(record.conversationNo);
                                }}
                              >
                                <div className="question-text">
                                  <div className="question-text-content">{record.query}</div>
                                </div>
                                {!isShare && (
                                  <div className={"w-auto box-border"}>
                                    <div className={"h-10 leading-10 overflow-hidden"}>
                                      {mouseLeaveIndex == index && <div className={"flex justify-end transform question-buttons mt-[-6px]"}>{/* 这个也不知干啥用的*/}</div>}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className={isShare ? "flex justify-between flex-1 share-content" : "flex justify-between flex-1"}>
                              <Checkbox
                                id={record.conversationNo}
                                className={"checked"}
                                checked={shareList.includes(record.conversationNo)}
                                onCheckedChange={() => handleToggleItem(record.conversationNo)}
                              ></Checkbox>
                              <div
                                className={isShare && shareList.includes(record.conversationNo) ? "check-right flex-1" : "flex-1"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleItem(record.conversationNo);
                                }}
                              >
                                {/* 渲染事件 */}
                                {record.reasoning.length > 0 ? <div className={isShare ? "p-2" : ""}>{historyThink(record, index)}</div> : ""}
                                <div className="message answer">
                                  <div className="content answer-content">
                                    <TypewriterMarkdown content={record.answer} speed={10} citations={record.citations} docs={record.files} conversationNo={record.conversationNo} />
                                  </div>
                                </div>
                                {
                                  <div className={"mx-[var(--mini-thread-content-inset)]"}>
                                    <div className="flex min-h-[46px] justify-start">
                                      <div className={" flex flex-wrap items-center gap-y-4 p-1 "}>
                                        {record.files.length > 0 ? (
                                          <span
                                            className="file-multi-info"
                                            onClick={() =>
                                              isSheetOpen
                                                ? handleSheetClose()
                                                : handleClick(
                                                    {
                                                      docs: record.files,
                                                      citations: record.citations,
                                                    },
                                                    record.conversationNo
                                                  )
                                            }
                                          >
                                            <div className="file-text py-[3px] px-[8px] cursor-pointer flex items-center">
                                              <MagnifyingGlassIcon className="w-[14px] h-[14px] mr-[6px]"></MagnifyingGlassIcon>
                                              {record.files.length}&nbsp;&nbsp;
                                              {t("status.data_items")}
                                              <ChevronRight className="w-[14px] h-[14px]  ml-[6px]" />
                                            </div>
                                          </span>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                }
                              </div>
                            </div>
                            {historyFeedback.index == index &&
                              !!historyFeedback.feedback &&
                              (!isSubmit ? (
                                <div className={"mt-[24px] w-full h-fit feedback dark:bg-[#303030]"}>
                                  <div className={"feedback-title"}>
                                    <div className={"title_container"}>
                                      <div className={"text_container"}>{historyFeedback.feedback == "LIKE" ? t("feedback.whatMakesYouSatisfied") : t("feedback.whatMakesYouDissatisfied")}</div>
                                    </div>
                                    <div className={"h-[16px]"}>
                                      <Separator orientation="vertical" />
                                    </div>

                                    <div className={"close"} onClick={closeFeedback}>
                                      <XMarkIcon className="w-[22px] h-[22px] text-[var(--text-text_primary)]"></XMarkIcon>
                                    </div>
                                  </div>
                                  <div className={"feedback_footer"}>
                                    <div className={"left"}>
                                      <div className={"options"}>
                                        {historyFeedback.feedback == "LIKE"
                                          ? FeedbackList.map((item) => (
                                              <div
                                                className={historyFeedback.detail.options?.includes(item.key) ? "option_item option_item_active" : "option_item"}
                                                onClick={() => {
                                                  console.log(item.key);
                                                  if (item.key) {
                                                    setHistoryFeedback((prev) => {
                                                      const options = prev.detail?.options || [];
                                                      // 只有当 options 中不存在 item.key 时才加入
                                                      console.log(options);
                                                      if (options.includes(item.key)) {
                                                        return {
                                                          ...prev,
                                                          detail: {
                                                            ...prev.detail,
                                                            options: options.filter((key) => key !== item.key),
                                                            other: "",
                                                          },
                                                        };
                                                      }
                                                      if (!options.includes(item.key)) {
                                                        return {
                                                          ...prev,
                                                          detail: {
                                                            ...prev.detail,
                                                            options: [...options, item.key],
                                                            other: "",
                                                          },
                                                        };
                                                      }
                                                      // 如果已存在，返回原状态不变
                                                      return prev;
                                                    });
                                                  }
                                                }}
                                              >
                                                <div className={"option_item_text"}>{item.value}</div>
                                              </div>
                                            ))
                                          : FeedbackDisList.map((item) => (
                                              <div
                                                className={historyFeedback.detail.options?.includes(item.key) ? "option_item option_item_active" : "option_item"}
                                                onClick={() => {
                                                  if (item.key) {
                                                    setHistoryFeedback((prev) => {
                                                      const options = prev.detail?.options || [];
                                                      // 只有当 options 中不存在 item.key 时才加入
                                                      console.log(options);
                                                      if (options.includes(item.key)) {
                                                        return {
                                                          ...prev,
                                                          detail: {
                                                            ...prev.detail,
                                                            options: options.filter((key) => key !== item.key),
                                                            other: "",
                                                          },
                                                        };
                                                      }
                                                      if (!options.includes(item.key)) {
                                                        return {
                                                          ...prev,
                                                          detail: {
                                                            ...prev.detail,
                                                            options: [...options, item.key],
                                                            other: "",
                                                          },
                                                        };
                                                      }
                                                      // 如果已存在，返回原状态不变
                                                      return prev;
                                                    });
                                                  }
                                                }}
                                              >
                                                <div className={"option_item_text"}>{item.value}</div>
                                              </div>
                                            ))}
                                      </div>
                                      {historyFeedback.detail.options.includes("OTHER") ? (
                                        <div className={"other"}>
                                          <textarea
                                            className={"text"}
                                            placeholder={t("feedback.tellUsMorePlaceholder")}
                                            value={historyFeedback.detail.other}
                                            onChange={(e) => {
                                              setHistoryFeedback((prev) => ({
                                                ...prev,
                                                detail: {
                                                  ...prev.detail,
                                                  other: e.target.value,
                                                },
                                              }));
                                            }}
                                          />
                                        </div>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                    <div className={"button"}>
                                      <Button
                                        size={"sm"}
                                        disabled={!historyFeedback.detail.options.length}
                                        onClick={() => onSubmit(record, historyFeedback)}
                                        className={"bg-blue-500 hover:bg-blue-600 dark:text-[#fff]"}
                                      >
                                        {t("feedback.submitFeedback")}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className={"mt-[24px] w-full h-fit feedback"}>
                                  <div className={"text-center p-[12px] bg-green-100 text-green-700"}>{t("feedback.thankYouMessage")}</div>
                                </div>
                              ))}
                          </div>
                        );
                      })}
                    {messages.map((messageGroup, mIndex) => {
                      const isAnswer = messageGroup.events.some((event) => event.event === AIEventType.ANSWER);
                      const think = messageGroup.events.some((event) => event.event === AIEventType.THINK);
                      const isEnd = messageGroup.events.some((event) => event.event === AIEventType.END);
                      let thinkStop = false;
                      let thinkLength = 0,
                        answerLength = 0;
                      let docIds: string[] = [];
                      if (think) {
                        const thinkGroup = messageGroup.events.filter((event) => event.event === AIEventType.THINK);
                        const thinks = thinkGroup[0]?.data.thinking || "";
                        thinkLength = thinks.split("\n").length;
                      }
                      if (isAnswer) {
                        const answerGroup = messageGroup.events.filter((event) => event.event === AIEventType.ANSWER);
                        const answer = answerGroup[0]?.data.answer || "";
                        answerLength = answer.split("\n").length;
                        const regex = /\[\^(\d+)\]/g;
                        const matches = new Set<string>();
                        let match;
                        while ((match = regex.exec(answer)) !== null) {
                          matches.add(match[1]);
                        }
                        console.log("matches", matches);
                        docIds = Array.from(matches);
                      }
                      const seach = messageGroup.events.filter((event) => event.event === AIEventType.SEARCH);

                      if (isEnd) {
                        const itemEnd = messageGroup.events.filter((event) => event.event === AIEventType.END)[0];
                        if (itemEnd.data.stopReason !== "COMPLETE" && answerLength == 0 && thinkLength > 0) {
                          thinkStop = true;
                        }
                      }
                      let searchData: SearchEventData = {
                        citations: [],
                        docs: [],
                      };
                      if (seach && seach.length) {
                        searchData = seach[0].data as SearchEventData;

                        if (searchData.citations.length) {
                          console.log("docIds", docIds);
                          const list = searchData.citations.filter((citation) => docIds.includes(citation.index.toString()));
                          console.log("list", list);

                          // 再用过滤后的 citations 过滤 docs
                          searchData.docs = searchData.docs.filter((doc) => list.some((citation) => citation.fileId === doc.fileId));
                        } else {
                          searchData.docs = [];
                        }
                      }
                      console.log("docs", searchData);
                      return (
                        <div key={messageGroup.conversationNo} className="conversation-group mb-5">
                          {/* 问题展示 */}
                          <div className={isShare ? "flex justify-between flex-1 share-content" : "flex justify-end flex-1"}>
                            {isShare && (
                              <Checkbox
                                id={messageGroup.conversationNo}
                                className={"checked"}
                                checked={shareList.includes(messageGroup.conversationNo)}
                                onCheckedChange={() => handleToggleItem(messageGroup.conversationNo)}
                              ></Checkbox>
                            )}
                            <div
                              className={isShare && shareList.includes(messageGroup.conversationNo) ? "check-right flex-1" : "flex-1"}
                              onMouseEnter={() => handleMouseout((history?.records?.length ?? 0) + mIndex)}
                              onMouseLeave={() => handleMouseLeave()}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleItem(messageGroup.conversationNo);
                              }}
                            >
                              <div className="question-text">
                                <div className="question-text-content">{messageGroup.question}</div>
                              </div>
                              {!isShare && (
                                <div className={"w-auto box-border"}>
                                  <div className={"h-10 leading-10 overflow-hidden"}>
                                    {mouseLeaveIndex == (history?.records?.length ?? 0) + mIndex && (
                                      <div className={"flex justify-end transform question-buttons mt-[-6px]"}>{/*  暂时不知道要做啥的*/}</div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          {/*  加载 */}

                          <div className={isShare ? "flex justify-between flex-1 share-content" : "flex justify-between flex-1"}>
                            <Checkbox
                              id={messageGroup.conversationNo}
                              className={"checked"}
                              checked={shareList.includes(messageGroup.conversationNo)}
                              onCheckedChange={() => handleToggleItem(messageGroup.conversationNo)}
                            ></Checkbox>

                            <div
                              className={isShare && shareList.includes(messageGroup.conversationNo) ? "check-right flex-1" : "flex-1"}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleItem(messageGroup.conversationNo);
                              }}
                            >
                              {thinkLength <= 0 && answerLength <= 0 && !isEnd ? (
                                <div className="space-y-2 w-full pb-40">
                                  <Loading />
                                  {/*<img src={} style={{color: 'black'}} alt=""/>*/}
                                  <Skeleton className="h-4 w-[250px]" />
                                  <Skeleton className="h-4 w-[200px]" />
                                </div>
                              ) : (
                                ""
                              )}
                              {/* 先渲染 THINK 事件 */}
                              {messageGroup.events
                                .filter((event) => event.event === AIEventType.THINK)
                                .map((event, index) => (
                                  <div className={"p-[12px]"}>
                                    <RenderEvent
                                      key={`event_${messageGroup.conversationNo}_THINK_${index}`}
                                      event={event}
                                      index={index}
                                      thinkStop={thinkStop}
                                      conversationNo={messageGroup.conversationNo}
                                      isLatestMessage={mIndex === messages.length - 1}
                                      citations={messagesCitations(messageGroup)}
                                      doc={messageDocs(messageGroup)}
                                      isThink={!isAnswer}
                                      messageGroup={messageGroup}
                                      groupIndex={mIndex}
                                      setMessages={setMessages}
                                    />
                                  </div>
                                ))}

                              {/* 再渲染除 THINK 外的其他事件 */}
                              {messageGroup.isThinkEnd &&
                                messageGroup.events
                                  .filter((event) => event.event !== AIEventType.THINK)
                                  .map((event, index) => (
                                    <RenderEvent
                                      key={`event_${messageGroup.conversationNo}_${event.event}_${index}`}
                                      event={event}
                                      index={index}
                                      conversationNo={messageGroup.conversationNo}
                                      isLatestMessage={mIndex === messages.length - 1}
                                      isEnd={isEnd}
                                      onUnderAnswer={(num) => setCurrent(num ?? 0)}
                                      citations={messagesCitations(messageGroup)}
                                      doc={messageDocs(messageGroup)}
                                      messageGroup={messageGroup}
                                      groupIndex={mIndex}
                                      setMessages={setMessages}
                                    />
                                  ))}
                              {messageGroup.isThinkEnd && isEnd ? (
                                <div className={"mx-[var(--mini-thread-content-inset)]"}>
                                  <div className="flex min-h-[46px] justify-start">
                                    <div className={" flex flex-wrap items-center gap-y-4 p-1 "}>
                                      {searchData.docs.length > 0 ? (
                                        <span
                                          className="file-multi-info"
                                          onClick={() =>
                                            isSheetOpen
                                              ? handleSheetClose()
                                              : handleClick(
                                                  {
                                                    docs: searchData.docs,
                                                    citations: searchData.citations,
                                                  },
                                                  messageGroup.conversationNo
                                                )
                                          }
                                        >
                                          <div className="file-text py-[3px] px-[8px] cursor-pointer flex items-center">
                                            <MagnifyingGlassIcon className="w-[14px] h-[14px] mr-[6px]"></MagnifyingGlassIcon>
                                            {searchData.docs.length}&nbsp;&nbsp;
                                            {t("status.data_items")}
                                            <ChevronRight className="w-[14px] h-[14px]  ml-[6px]" />
                                          </div>
                                        </span>
                                      ) : (
                                        " "
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                ""
                              )}
                              {feedbackList.index == mIndex &&
                                !!feedbackList.feedback &&
                                (!isSubmit ? (
                                  <div className={"mt-[24px] w-full h-fit feedback"}>
                                    <div className={"feedback-title"}>
                                      <div className={"title_container"}>
                                        <div className={"text_container"}>{feedbackList.feedback == "LIKE" ? t("feedback.whatMakesYouSatisfied") : t("feedback.whatMakesYouDissatisfied")}</div>
                                      </div>
                                      <div className={"h-[16px]"}>
                                        <Separator orientation="vertical" />
                                      </div>

                                      <div className={"close"} onClick={closeFeedback}>
                                        <XMarkIcon className="w-[22px] h-[22px] text-[var(--text-text_primary)"></XMarkIcon>
                                      </div>
                                    </div>
                                    <div className={"feedback_footer"}>
                                      <div className={"left"}>
                                        <div className={"options"}>
                                          {feedbackList.feedback == "LIKE"
                                            ? FeedbackList.map((item) => (
                                                <div
                                                  className={feedbackList.detail.options?.includes(item.key) ? "option_item option_item_active" : "option_item"}
                                                  onClick={() => {
                                                    if (item.key) {
                                                      setFeedbackList((prev) => {
                                                        const options = prev.detail?.options || [];
                                                        // 只有当 options 中不存在 item.key 时才加入
                                                        console.log(options);
                                                        if (options.includes(item.key)) {
                                                          return {
                                                            ...prev,
                                                            detail: {
                                                              ...prev.detail,
                                                              options: options.filter((key) => key !== item.key),
                                                              other: "",
                                                            },
                                                          };
                                                        }
                                                        if (!options.includes(item.key)) {
                                                          return {
                                                            ...prev,
                                                            detail: {
                                                              ...prev.detail,
                                                              options: [...options, item.key],
                                                              other: "",
                                                            },
                                                          };
                                                        }
                                                        // 如果已存在，返回原状态不变
                                                        return prev;
                                                      });
                                                    }
                                                  }}
                                                >
                                                  <div className={"option_item_text"}>{item.value}</div>
                                                </div>
                                              ))
                                            : FeedbackDisList.map((item) => (
                                                <div
                                                  className={feedbackList.detail.options?.includes(item.key) ? "option_item option_item_active" : "option_item"}
                                                  onClick={() => {
                                                    if (item.key) {
                                                      setFeedbackList((prev) => {
                                                        const options = prev.detail?.options || [];
                                                        // 只有当 options 中不存在 item.key 时才加入
                                                        console.log(options);
                                                        if (options.includes(item.key)) {
                                                          return {
                                                            ...prev,
                                                            detail: {
                                                              ...prev.detail,
                                                              options: options.filter((key) => key !== item.key),
                                                              other: "",
                                                            },
                                                          };
                                                        }
                                                        if (!options.includes(item.key)) {
                                                          return {
                                                            ...prev,
                                                            detail: {
                                                              ...prev.detail,
                                                              options: [...options, item.key],
                                                              other: "",
                                                            },
                                                          };
                                                        }
                                                        // 如果已存在，返回原状态不变
                                                        return prev;
                                                      });
                                                    }
                                                  }}
                                                >
                                                  <div className={"option_item_text"}>{item.value}</div>
                                                </div>
                                              ))}
                                        </div>
                                        {feedbackList.detail.options.includes("OTHER") ? (
                                          <div className={"other"}>
                                            <textarea
                                              className={"text"}
                                              placeholder={"(可选) 告诉我们更多关于你的使用体验"}
                                              value={feedbackList.detail.other}
                                              onChange={(e) => {
                                                setFeedbackList((prev) => ({
                                                  ...prev,
                                                  detail: {
                                                    ...prev.detail,
                                                    other: e.target.value,
                                                  },
                                                }));
                                              }}
                                            />
                                          </div>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className={"button"}>
                                        <Button
                                          size={"sm"}
                                          disabled={!feedbackList.detail.options.length}
                                          onClick={() => onSubmit(messageGroup, feedbackList)}
                                          className={"bg-blue-500 hover:bg-blue-600 dark:text-[#fff]"}
                                        >
                                          提交
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className={"mt-[24px] w-full h-fit feedback"}>
                                    <div className={"text-center p-[12px] bg-green-100 text-green-700"}>感谢你帮助我们变得更好</div>
                                  </div>
                                ))}
                              {/* 推荐事件 */}
                              {messageGroup.isThinkEnd &&
                                messageGroup.events.filter((event) => event.event === AIEventType.RECOMMEND).map((event) => recommendEvent("recommend" in event.data ? event.data.recommend : []))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="inner">
                    <div className={userScrolled ? "to-bottom-button" : "to-bottom-button hidden-button"} onClick={scrollToBottom}>
                      {pollingActive.current ? <SvgLoading className="loading-border"></SvgLoading> : ""}
                      <ChevronDown></ChevronDown>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div></div>
        </div>
      ) : null}
      {isSheetOpen ? sheet() : ""}
      <EditTitleDialog dialogTitle={topicTitle || history?.topicName} isOpen={isEdit} topicNo={topicNo || paramsId} onOpenChange={setIsEdit} onChangeSuccess={onChangeSuccess}></EditTitleDialog>
      {<ViewFile open={isViewOpen && !isShare} type={viewFile.type} record={viewFile.record} index={viewFile.index} onClose={handleClose}></ViewFile>}
    </div>
  );
}
